import { useQuery } from 'react-query';

import type { InvoiceRequest } from '@finance-review/models/invoice';
import { useCompany } from 'modules/app/hooks/useCompany';
import { type QueryState } from 'src/core/api/queryState';
import { mapQueryResultToQueryState } from 'src/core/api/utils/queryState';

export type CardPaymentEligibilityQueryParams = {
  amount_declared?: number;
  currency_declared?: string;
};

const getCardPaymentEligibility = async ({
  companyId,
  invoiceRequestId,
  query,
}: {
  companyId: string;
  invoiceRequestId: string;
  query?: CardPaymentEligibilityQueryParams;
}): Promise<CardPaymentEligibility> => {
  const params = new URLSearchParams();
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });

  const response = await fetch(
    `${appConfig.apiUrls.apiV2}/${companyId}/cards/invoices/${invoiceRequestId}/card-payment-eligibility?${params.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch card payment eligibility: ${response.statusText}`,
    );
  }

  return response.json();
};

export type CardPaymentEligibility = {
  data: {
    eligible: boolean;
    reason?: string;
    details?: {
      amountDeclared?: number;
      amountLimit?: number;
    };
  };
};

export const useCardPaymentEligibilityQuery = (
  invoiceRequestId: InvoiceRequest.EntityId | undefined,
  options?: {
    isEnabled?: boolean;
    query?: CardPaymentEligibilityQueryParams;
  },
): QueryState<CardPaymentEligibility> => {
  const company = useCompany();
  const isEnabled =
    Boolean(company.id) &&
    Boolean(invoiceRequestId) &&
    (options?.isEnabled ?? true);

  const result = useQuery(
    [
      'invoice_requests',
      company.id,
      invoiceRequestId,
      'card-payment-eligibility',
      options?.query,
    ],
    async (): Promise<CardPaymentEligibility> => {
      if (!invoiceRequestId) {
        throw new Error('Invoice request ID is required');
      }
      return getCardPaymentEligibility({
        companyId: company.id,
        invoiceRequestId,
        query: options?.query,
      });
    },
    {
      enabled: isEnabled,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  return mapQueryResultToQueryState(result);
};
